import {
  CustomInvoiceAdminDto,
  InvoiceAdminDto,
  LocationPetProfileAdminDto,
  ReceiptDto,
  TagAdminDto,
} from '../types/api'

export const MAX_BATCH_SIZE = 10
export const MAX_BULK_SIZE = 100

export interface TFileImage {
  assetId: string
  deleted: boolean
  file?: File & { preview: string }
  url?: string
}

export interface TUserInvitation {
  email?: string
  role: string
  divisions: string[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GenericSimpleBag = Record<string, any>

export type TBookingStep = 'BOOKING' | 'INTAKE' | 'PAYMENT' | 'CONFIRMATION'
export interface TBookingSearchOptions {
  startDate?: string
  endDate?: string
  petGroups: TBookingSearchPetGroup[]
  locationServiceType?: string
  type?: string
  offersType: 'PRIMARY' | 'SERVICE' | 'PET' | undefined
  offerName?: string
  displayName?: string
  offerGroupId?: string
  locationUserProfileId?: string
  subServiceType?: string
  limit?: number
  nextToken?: string
  required?: boolean // for Required Addons, to only get required addons
  cacheEnabled?: boolean
  enabled?: boolean
}

export interface TBookingSearchPetGroup {
  locationSpecies: string | null
  petCount: number
  petWeights?: number[]
  temperTested?: boolean[]
  parentOffer?: string
  serviceTypeName?: string
  overridePrimaryResourceId?: string
}

export interface TOrderSearchOptions {
  limit?: number
  nextToken?: string
  name?: string
  orderStatus?: string
  owner?: { id?: string }
  locationUserProfile?: string
  createdBy?: string
  primaryServiceType?: string
  sort?: string
  order?: 'asc' | 'desc'
  dynamic?: string
  includes?: string[]
  hasSharedCRM?: boolean
}

export interface TInvoiceSearchOptions {
  dynamic?: string
  includes?: string[]
  limit?: number
  'order.locationUserProfileId'?: string
  name?: string
  nextToken?: string
  invoiceStatus?: string
  'items.locationServiceTypeId'?: string
  sortOrder?: 'asc' | 'desc'
}

export interface TBookingInvoiceSearchOptions {
  sortOrder?: 'asc' | 'desc'
  sortColumn?: string
  limit?: number
  nextToken?: string
  includes?: string[]
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export interface TInvoiceListOptions {
  visitDate?: string
  visitStatus?: 'ARRIVING' | 'HERE' | 'DEPARTING'
  visitSubStatus?: 'HERE' | 'NOT_HERE'
  invoiceStatus?: Status[]
  invoiceSubStatus?: SubStatus[]
  limit?: number
  nextToken?: string | null
  sort?: string
  sortOrder?: SortOrder
  order?: string
  'items.locationServiceTypeId'?: string
  'items.offerType'?: string
  'items.offerTypeFilter'?: string
  'order.locationUserProfileId'?: string
  'order.orderStatus'?: string
  'period.startDate'?: string
  'period.endDate'?: string
  'resourceUsages.resourceId'?: string
  distinguishes?: string[] | null
  includes?: string[]
  dynamic?: string
  hasSharedCRM?: boolean
  notUseDefaultIncludes?: boolean
  isInStay?: boolean
  locationServiceType?: string
  period?: string[]
  'petRelations.invoicePet.locationPetProfileId'?: string[]
  'locationServiceType.serviceType.isVisit'?: boolean
}

export type TInvoiceSubStatus = InvoiceAdminDto['invoiceSubStatus']

export enum Status {
  ALL_STATUS = 'all-statuses',
  CANCELLED = 'CANCELED',
  CONFIRMED = 'CONFIRMED',
  PARTIALLY_CANCELLED = 'PARTIALLY_CANCELED',
}

export enum SubStatus {
  REQUESTED = 'REQUESTED',
  SCHEDULED = 'SCHEDULED',
  DECLINED = 'DECLINED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELED',
  PENDING_SCHEDULED = 'PENDING_SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  PENDING = 'PENDING',
  BOOKED = 'BOOKED',
  SOLD = 'SOLD',
}

export enum ActivityStatus {
  PLANNED = 'PLANNED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETED',
}

export interface TResourceUsagesOptions {
  startDate?: string
  endDate?: string
  invoice?: string
  serviceId?: string[]
  limit?: number
  status?: string
  nextToken?: string
  includes?: string[]
  distinguishes?: string[]
  'resource.type'?: string[]
  'invoiceItem.locationServiceTypeId'?: string
  'service.isPrimary'?: boolean
}

// TODO: use a generic interface and not one dependent on a custom shape
export interface TDivisionSchema {
  displayName: string
  description: string
  parentDivisionId: string
}

export interface TLocationSchema {
  displayName: string
  divisionId: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TOfferState = Record<string, any>
export type TOfferSchedulingRule = 'CHECK_IN' | 'LAST_NIGHT' | 'CHECK_OUT'

export interface TOfferService {
  id: string
  joinId?: string
  displayName: string
  frequency: string
  quantity: string
  unitRate: string
  itemizedRate: string
  schedulingRule: TOfferSchedulingRule
}

export interface TQueryConfig {
  enabled?: boolean
}

export const ContractType = {
  ServiceAgreement: 'SERVICE_AGREEMENT',
  PaymentSurchargeAgreement: 'PAYMENT_SURCHARGE_AGREEMENT',
} as const

export type TContractType = (typeof ContractType)[keyof typeof ContractType]

export type TPaymentPayFacType =
  | 'TILLED'
  | 'OFFLINE'
  | 'ADYEN'
  | 'CASH'
  | 'OTHER'
  | 'ADYEN_CARD_PRESENT'
  | 'VOUCHER_OFFER'
  | 'VOUCHER_CASH'

export interface TInvoicePetHistory {
  orderId: string
  invoices: CustomInvoiceAdminDto[]
}

export interface TInvoicePetHistoryList {
  [key: string]: {
    pet: LocationPetProfileAdminDto
    history: TInvoicePetHistory[]
  }
}

// TODO: remove after Receipt api is updated
export interface ReceiptDtoCustom extends ReceiptDto {
  contact?: {
    name?: string
    email?: string
    phone?: string
    address?: string[]
  }
  vet?: {
    name?: string
    phone?: string
  }
}

export type TBulkUpdateData<T> = {
  ids: string[]
  data: T
}

export type TVoucherOfferTransaction = {
  invoiceId: string
  invoiceItemId: string
  voucherIds: string[]
}

export type TTasksListItem = {
  locationName: string
  petId: string
  petUrl: string
  petName: string
  petOwnerLastName: string
  petSex: string
  petWeight: number
  petBirthday: string
  petSpecies: string
  petBreed: string
  petAge: number
  petColor: string
  petAlert: string
  tags: TagAdminDto[]
  userId: string
  userUrl: string
  userEmail: string
  userPhone: string
  userFirstName: string
  userLastName: string
  userAddress: string
  userIsOnline: string
  userCreatedLocation: string
  lupCreatedAt: string
  invoiceId: string
  primaryOffer: string
  startDate: string
  endDate: string
  endTime: string
  space: string
  activity: string
  careInstructions: string
}

export interface TCustomerAddress {
  streetAddress: string
  streetAddress2?: string
  city: string
  state: string
  zipCode: string
  country: string
}

export type TSMSConsentResponse = {
  success: boolean
  data: {
    smsConsentStatus: 'ACTIVE' | 'INACTIVE'
    smsConsentStatusSource: 'ADMIN' | 'CUSTOMER' | 'UNKNOWN'
  }
}

export type TSMSConsentRequest = {
  consentType: 'SMS'
  lupId: string
  phoneNumber?: string
  operatorEmail: string
  consentStatus: 'ACTIVE' | 'INACTIVE'
}

export type TCustomInvoiceDto = {
  id?: string
  purchaseDate?: string
  service?: string | null
  pet?: {
    id?: string | null
    displayName?: string | null
  }
  invoiceStatus?: 'PENDING' | 'PENDING_PAYMENT' | 'CONFIRMED' | 'CANCELED' | 'PARTIALLY_CANCELED' | 'FAILED'
  invoiceSubStatus?:
    | 'PENDING'
    | 'CANCELED'
    | 'DECLINED'
    | 'REQUESTED'
    | 'SCHEDULED'
    | 'COMPLETED'
    | 'PENDING_SCHEDULED'
    | 'IN_PROGRESS'
    | 'BOOKED'
    | 'SOLD'
    | null
  serviceDate?: string
  orderId?: string
}
